import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from 'sonner'
import type {
  Landing,
  LandingListResponse,
  LandingAnalytics,
  CreateLandingRequest,
  UpdateLandingRequest,
  LandingPreviewRequest,
} from '@/types'

const KEY = 'landing-pages'

export interface LandingFilters {
  status?: string
  search?: string
  page?: number
  limit?: number
}

export const useLandings = (filters: LandingFilters = {}) => {
  return useQuery<LandingListResponse>({
    queryKey: [KEY, 'list', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {}
      if (filters.status) params.status = filters.status
      if (filters.search) params.search = filters.search
      if (filters.page) params.page = filters.page
      if (filters.limit) params.limit = filters.limit
      const { data } = await api.get('/landing-pages', { params })
      return data
    },
  })
}

export const useLanding = (id: string) => {
  return useQuery<Landing>({
    queryKey: [KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/landing-pages/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useCreateLanding = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateLandingRequest) => {
      const { data } = await api.post('/landing-pages', body)
      return data as Landing
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
      toast.success('Landing creada')
    },
  })
}

export const useUpdateLanding = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateLandingRequest }) => {
      const { data } = await api.patch(`/landing-pages/${id}`, updates)
      return data as Landing
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [KEY] })
      qc.invalidateQueries({ queryKey: [KEY, variables.id] })
    },
  })
}

export const useDeleteLanding = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/landing-pages/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
      toast.success('Landing eliminada')
    },
  })
}

export const usePublishLanding = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/landing-pages/${id}/publish`)
      return data as Landing
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [KEY] })
      qc.invalidateQueries({ queryKey: [KEY, id] })
      toast.success('Landing publicada')
    },
  })
}

export const useArchiveLanding = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/landing-pages/${id}/archive`)
      return data as Landing
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [KEY] })
      qc.invalidateQueries({ queryKey: [KEY, id] })
      toast.success('Landing archivada')
    },
  })
}

export const useDuplicateLanding = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/landing-pages/${id}/duplicate`)
      return data as Landing
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
      toast.success('Landing duplicada')
    },
  })
}

export const useLandingAnalytics = (id: string, period: string) => {
  return useQuery<LandingAnalytics>({
    queryKey: [KEY, id, 'analytics', period],
    queryFn: async () => {
      const { data } = await api.get(`/landing-pages/${id}/analytics`, { params: { period } })
      return data
    },
    enabled: !!id,
  })
}

export async function fetchLandingPreview(body: LandingPreviewRequest): Promise<string> {
  const { data } = await api.post('/landing-pages/preview', body)
  return data.html as string
}

/** Build the public URL for a landing from the configured API base. */
export function landingPublicUrl(slug: string, publicUrl?: string | null): string {
  if (publicUrl) return publicUrl
  const base = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '')
  return `${base}/l/${slug}`
}
